import { Box, Button, Flex, Link, Skeleton, Text, Textarea } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { useEffect, useRef, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { DetailSheet } from '../../../components/DetailSheet'
import { EmptyCell } from '../../../components/EmptyCell'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useAppToast, formatPhone } from '../../../helpers'
import { HrNoteService } from '../../../services/HrNote/HrNoteService'
import { JobApplicationService } from '../../../services/JobApplication/JobApplicationService'
import { JobApplicationComplete } from '../../../services/JobApplication/types'
import { HrNoteHistoryModal } from './HrNoteHistoryModal'
import { vacancyLabel } from './vacancyLabel'

interface IProps {
  /** `null` enquanto nenhuma candidatura está aberta — o modal fica montado e animado. */
  id: number | null
  onClose: () => void
}

export function JobApplicationViewModal({ id, onClose }: IProps) {
  const service = new JobApplicationService()
  const hrNoteService = new HrNoteService()
  const [jobApplication, setJobApplication] = useState<JobApplicationComplete>()
  const [loading, setLoading] = useState(false)
  const [hrNote, setHrNote] = useState('')
  const [hrNoteInitial, setHrNoteInitial] = useState('')
  const [hrNoteSaving, setHrNoteSaving] = useState(false)
  const [isHrNoteHistoryOpen, setIsHrNoteHistoryOpen] = useState(false)
  const toast = useAppToast()
  const hrNoteRef = useRef<HTMLTextAreaElement>(null)
  /**
   * O `id` volta a `null` assim que o modal começa a fechar, mas o `Textarea` só sai do DOM ao fim da
   * animação de saída — e é essa remoção que dispara o `blur` que salva a nota. Guardar o último id
   * válido é o que mantém o salvamento ao fechar com Esc.
   */
  const lastIdRef = useRef<number | null>(null)

  const adjustHrNoteHeight = () => {
    const element = hrNoteRef.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight}px`
  }

  useEffect(() => {
    adjustHrNoteHeight()
  }, [hrNote])

  useEffect(() => {
    if (id === null) return

    lastIdRef.current = id
    let isActive = true

    setJobApplication(undefined)
    setHrNote('')
    setHrNoteInitial('')

    service
      .get(id)
      .then((response) => {
        if (!isActive) return
        setJobApplication(response)
      })
      .catch((error) => {
        // Sem isto a falha deixa o modal aberto com o título em `-` e a ficha em esqueleto para
        // sempre, sem aviso e sem outra saída além do Fechar.
        if (!isActive) return
        toast.error('Erro ao carregar a candidatura', error)
        onClose()
      })

    hrNoteService
      .get(id)
      .then((response) => {
        if (!isActive) return
        setHrNote(response.note)
        setHrNoteInitial(response.note)
      })
      .catch(() => {
        if (!isActive) return
        toast.error('Erro ao carregar Nota RH')
      })

    return () => {
      isActive = false
    }
  }, [id])

  const handleHrNoteBlur = async () => {
    const targetId = lastIdRef.current
    if (targetId === null || hrNote === hrNoteInitial) return

    setHrNoteSaving(true)
    try {
      const response = await hrNoteService.save(targetId, hrNote)
      setHrNote(response.note)
      setHrNoteInitial(response.note)
      toast.success('Nota RH salva')
    } catch (error) {
      toast.error('Erro ao salvar Nota RH', error)
    } finally {
      setHrNoteSaving(false)
    }
  }

  /**
   * Send to Clint button click handler
   */
  const handleClintButtonClick = () => {
    if (id === null) return

    setLoading(true)
    service
      .sendToActiveCampaign(id)
      .then((response) => {
        setJobApplication((p) => (p ? { ...p, is_sent_to_clint: response.isSentToClint } : p))
        toast.success('Enviado para a Clint com sucesso')
      })
      .catch(() => {
        toast.error('Erro ao enviar para a Clint')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  `

  const isFetching = !jobApplication

  const freeText = (value?: string | null) => <EmptyCell value={value} />

  return (
    <>
      <FormModal
        isOpen={id !== null}
        onClose={onClose}
        size="wide"
        title={
          <Skeleton isLoaded={!isFetching} minH="24px" minW="220px">
            {jobApplication?.name || '-'}
          </Skeleton>
        }
      >
        <Flex justify="flex-end" gap={3} mb={4}>
          <Tooltip label={jobApplication?.is_sent_to_clint ? 'Reenviar para a Clint' : 'Enviar para a Clint'}>
            <Button
              aria-label={jobApplication?.is_sent_to_clint ? 'Reenviar para a Clint' : 'Enviar para a Clint'}
              colorScheme={jobApplication?.is_sent_to_clint ? 'green' : 'magenta'}
              variant="outline"
              size="sm"
              onClick={handleClintButtonClick}
              isDisabled={loading || isFetching}
            >
              <Box
                animation={loading ? `${spin} 1s linear infinite` : 'none'}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <img src="/img/icons/clint.png" alt="" style={{ width: 25, height: 25 }} />
              </Box>
            </Button>
          </Tooltip>
          {jobApplication?.document && (
            <ActionButton action="download" label="Ver currículo" showLabel href={jobApplication.document} size="sm" />
          )}
        </Flex>

        <DetailSheet
          isLoading={isFetching}
          fields={[
            { label: 'Vaga', value: jobApplication && vacancyLabel(jobApplication) },
            { label: 'Áreas', value: jobApplication?.area?.join(', ') },
            { label: 'Telefone', value: formatPhone(jobApplication?.phone) },
            {
              label: 'E-mail',
              value: jobApplication && (
                <Link href={`mailto:${jobApplication.email}`} color="blue.500">
                  {jobApplication.email}
                </Link>
              )
            },
            {
              label: 'Portfólio',
              value: jobApplication?.portfolio_link ? (
                <Link href={jobApplication.portfolio_link} color="blue.500" isExternal>
                  {jobApplication.portfolio_link}
                </Link>
              ) : undefined
            },
            { label: 'Faixa Salarial', value: jobApplication?.salary_range?.range },
            { label: 'Enviado em', value: jobApplication?.created_at && formatDateTime(jobApplication.created_at) }
          ]}
        />

        {!isFetching && (
          <>
            <Box mt={6}>
              <Text fontWeight="bold">Por que você quer trabalhar na ABRA?</Text>
              <Text textAlign="justify">{freeText(jobApplication.why_you_want_to_work_here)}</Text>
            </Box>
            <Box mt={4}>
              <Text fontWeight="bold">Cite alguns motivos para te contratarmos</Text>
              <Text textAlign="justify">{freeText(jobApplication.reasons_to_hire_you)}</Text>
            </Box>
          </>
        )}

        <Box mt={6} p="4" borderWidth="1px" borderRadius="md" bg="gray.50">
          <FormField label="Nota RH" htmlFor="hr-note" helperText="Salva ao sair do campo." m={0}>
            <Textarea
              ref={hrNoteRef}
              id="hr-note"
              size="sm"
              value={hrNote}
              onChange={(e) => setHrNote(e.target.value)}
              onBlur={handleHrNoteBlur}
              placeholder="Adicione uma anotação sobre este candidato."
              rows={5}
              minH="8rem"
              maxH="56vh"
              resize="none"
              overflowY="auto"
              bg="white"
              isDisabled={hrNoteSaving || isFetching}
            />
          </FormField>
          <Flex justify="flex-end" mt={2}>
            <Button
              size="xs"
              variant="outline"
              leftIcon={<Icon name="lni lni-timer" />}
              onClick={() => setIsHrNoteHistoryOpen(true)}
              isDisabled={isFetching}
            >
              Histórico
            </Button>
          </Flex>
        </Box>
      </FormModal>

      {id !== null && (
        <HrNoteHistoryModal
          isOpen={isHrNoteHistoryOpen}
          onClose={() => setIsHrNoteHistoryOpen(false)}
          jobApplicationId={id}
        />
      )}
    </>
  )
}
